import React from "react";
import styled, { keyframes } from "styled-components";

interface Props {
  size?: number; // Width/height of loader in px
  color?: string; // Dot color
  duration?: number; // Animation duration in seconds
}

const dropInOut = (distance: number) => keyframes`
  0% {
    opacity: 0;
    transform: translateY(-${distance}px) translateX(${distance}px);
  }
  20% {
    opacity: 1;
    transform: translateY(0) translateX(0);
  }
  70% {
    opacity: 1;
    transform: translateY(0) translateX(0);
  }
  100% {
    opacity: 0;
    transform: translateY(${distance}px) translateX(0);
  }
`;

const LoaderWrapper = styled.div<{ size: number }>`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;

const Dot = styled.div<{
  top: number;
  left: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
}>`
  position: absolute;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  opacity: 0;
  animation: ${({ size }) => dropInOut(size * 1.5)}
    ${({ duration }) => duration}s infinite ease-in-out;
  animation-delay: ${({ delay }) => delay}s;
  top: ${({ top }) => top}%;
  left: ${({ left }) => left}%;
  transform: translate(-50%, -50%);
`;

export const Spinner: React.FC<Props> = ({
  size = 24,
  color = "#022CAB",
  duration = 3,
}) => {
  const dotSize = (size / 60) * 16;

  // Minimum triangle layout
  const positions = [
    { top: 10, left: 70, delay: 0 },
    { top: 40, left: 40, delay: 0.2 },
    { top: 40, left: 70, delay: 0.1 },
    { top: 70, left: 10, delay: 0.5 },
    { top: 70, left: 40, delay: 0.4 },
    { top: 70, left: 70, delay: 0.3 },
  ];

  return (
    <LoaderWrapper
      size={size}
      role="progressbar"
      aria-label="Loading..."
      data-testid="spinner"
    >
      {positions.map(({ top, left, delay }, i) => (
        <Dot
          key={i}
          top={top}
          left={left}
          size={dotSize}
          color={color}
          delay={delay}
          duration={duration}
        />
      ))}
    </LoaderWrapper>
  );
};
